import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import ProductInfoTooltip from './ProductInfoTooltip/ProductInfoTooltip';
import { MdLock } from 'react-icons/md';
import { useCDK } from 'lib/hooks/useCdk';

export type StyleObject = {
  menuWrapper?: React.CSSProperties;
  menuItem?: React.CSSProperties;
  itemIcon?: React.CSSProperties;
  itemTitle?: React.CSSProperties;
};

type MenuWrapperProps = {
  isMenuOpen: boolean;
};

const MenuWrapper = styled.div<MenuWrapperProps>`
  background: ${({ theme }) => theme.colors.white[100]};
  border: ${({ theme }) => `1px solid ${theme.colors.neutral[10]}`};
  box-sizing: border-box;
  box-shadow:
    0px 6px 12px 0px rgba(66, 79, 104, 0.06),
    0px 0px 4px 0px rgba(66, 79, 104, 0.08);
  border-radius: 8px;
  display: ${props => (props.isMenuOpen ? 'block' : 'none')};
  z-index: 10; /** SUS-1289 && SUS-2567 changes **/
  position: absolute;

  .cdk-user > div {
    color: ${({ theme }) => theme.colors.primary[100]};
  }

  .cdk-user > div > svg {
    fill: ${({ theme }) => theme.colors.primary[60]};
  }
`;

const ItemIcon = styled.div`
  margin-left: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
`;
type ItemTitleProps = {
  color: string;
};
const ItemTitle = styled.div<ItemTitleProps>`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${props => props.color};
  margin-left: 10px;
`;
const ItemSubtitle = styled.div`
  color: ${({ theme }) => theme.colors.secondary[40]};
  font-weight: 400;
  font-size: 14px;
  margin-left: 10px;
`;

export interface Data {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  onClick?: (id: string) => void;
  titleColor?: string;
  isDisabled?: boolean;
  productFeatureId?: number;
  productTooltipStyle?: React.CSSProperties;
  nextPlan?: string;
  showNonFreemiumTooltip?: boolean;
  tooltipText?: string;
  dataCy?: string;
  showLockIcon?: boolean;
  customItem?: React.ReactNode;
}

type Props = {
  isMenuOpen: boolean;
  items: Array<Data>;
  setIsMenuOpen: (b: any) => void;
  id: string;
  className?: string;
  reference?: any;
  width?: string;
  dataCy?: string;
  extendStyles?: StyleObject;
};

type ItemProps = {
  width?: string;
  isDisabled?: boolean;
};

export const Item = styled.div<ItemProps>`
  display: flex;
  height: 40px;
  align-items: center;
  width: ${props => props.width || 'auto'};
  min-width: 200px;
  background: ${({ theme }) => theme.colors.white[100]};
  ${props =>
    !props.isDisabled &&
    `&:hover {
    background: ${props.theme.colors.secondary[5]};
  }`}
  z-index: 100;
  ${props => (props.isDisabled ? `cursor: not-allowed;` : `cursor: pointer;`)}
`;

export const ListDropdownMenu = ({
  isMenuOpen,
  items,
  setIsMenuOpen,
  id,
  className,
  reference,
  width = 'auto',
  dataCy,
  extendStyles,
}: Props) => {
  const { isCdkUser } = useCDK();
  const { ref } = useOnClickOutside(() => setIsMenuOpen(false));
  const { colors } = useTheme();

  return (
    <MenuWrapper
      ref={reference ? reference : ref}
      isMenuOpen={isMenuOpen}
      {...(className ? { className: className } : {})}
      data-cy={dataCy}
      style={extendStyles?.menuWrapper}
    >
      {items.map((item, i) =>
        item.isDisabled ? (
          <ProductInfoTooltip
            productFeatureId={item.productFeatureId}
            direction='left'
            style={{
              right: '200px',
              ...item.productTooltipStyle,
            }}
            key={item.title}
            nextPlan={item.nextPlan}
            isNonFreemiumTooltip={item.showNonFreemiumTooltip}
            tooltipText={item.tooltipText}
            visibility={!!item.showLockIcon}
          >
            <Item
              width={width}
              key={i}
              isDisabled={item.isDisabled}
              className={`${isCdkUser ? 'cdk-user' : ''}`}
            >
              <ItemIcon style={{ opacity: 0.5, ...extendStyles?.itemIcon }}>
                {item.icon}
              </ItemIcon>
              <ItemTitle
                color={item.titleColor ? item.titleColor : colors.black[100]}
                style={{
                  marginRight: '10px',
                  opacity: 0.5,
                  ...extendStyles?.itemTitle,
                }}
              >
                {item.title}
              </ItemTitle>

              {!item.showNonFreemiumTooltip && !!item.showLockIcon && (
                <MdLock size={18} />
              )}
            </Item>
          </ProductInfoTooltip>
        ) : !!item.customItem ? (
          item.customItem
        ) : (
          <Item
            width={width}
            key={i}
            data-cy={item?.dataCy}
            onClick={() => {
              item.onClick && item.onClick(id);
              reference ? setIsMenuOpen(0) : setIsMenuOpen(false);
            }}
            className={`${isCdkUser ? 'cdk-user' : ''}`}
          >
            <ItemIcon>{item.icon}</ItemIcon>
            <ItemTitle
              color={item.titleColor ? item.titleColor : colors.black[100]}
              style={extendStyles?.itemTitle}
            >
              {item.title}
            </ItemTitle>
            {item.subtitle !== '' && (
              <ItemSubtitle>{item.subtitle}</ItemSubtitle>
            )}
          </Item>
        )
      )}
    </MenuWrapper>
  );
};
